import React, { useState } from 'react';
import { Search, BookOpen, TrendingUp, Code, Hash } from 'lucide-react';

interface GlossaryTerm {
  term: string;
  aliases?: string[];
  category: 'math' | 'finance' | 'functional' | 'technical';
  definition: string;
  example?: string;
  formula?: string;
}

const glossaryTerms: GlossaryTerm[] = [
  // Math Terms
  {
    term: "Optimization",
    aliases: ["Mathematical Optimization", "Mathematical Programming"],
    category: "math",
    definition: "The process of finding the best solution from all feasible solutions, typically by maximizing or minimizing an objective function subject to constraints.",
    example: "Finding the price that maximizes profit while maintaining minimum margin requirements."
  },
  {
    term: "Objective Function",
    aliases: ["Cost Function", "Loss Function", "Fitness Function"],
    category: "math",
    definition: "A mathematical function that defines the quantity to be maximized or minimized in an optimization problem.",
    example: "In pricing optimization: maximize profit = (price - cost) × demand(price)",
    formula: "max/min f(x) subject to constraints"
  },
  {
    term: "Constraint",
    aliases: ["Restriction", "Bound", "Limitation"],
    category: "math",
    definition: "A condition that must be satisfied by any feasible solution to an optimization problem.",
    example: "Price must be at least 20% above cost: price ≥ 1.20 × cost",
    formula: "g(x) ≤ b or h(x) = c"
  },
  {
    term: "Linear Programming (LP)",
    aliases: ["LP", "Linear Optimization"],
    category: "math",
    definition: "An optimization technique where both the objective function and constraints are linear functions of the decision variables.",
    example: "Optimizing production quantities where cost and profit are proportional to quantities produced.",
    formula: "max c'x subject to Ax ≤ b, x ≥ 0"
  },
  {
    term: "Mixed-Integer Programming (MIP)",
    aliases: ["MIP", "MILP", "Integer Programming"],
    category: "math",
    definition: "Optimization where some decision variables must take integer values (whole numbers) while others can be continuous.",
    example: "Deciding how many facilities to open (integer) and how much to produce at each (continuous).",
    formula: "max c'x subject to Ax ≤ b, x_i ∈ ℤ for i ∈ I"
  },
  {
    term: "Nonlinear Programming (NLP)",
    aliases: ["NLP", "Nonlinear Optimization"],
    category: "math",
    definition: "Optimization where the objective function or constraints contain nonlinear terms such as squares, exponentials, or products of variables.",
    example: "Pricing optimization where demand = a × price^(-elasticity) is a nonlinear function.",
    formula: "max f(x) subject to g(x) ≤ 0, where f or g is nonlinear"
  },
  {
    term: "Dynamic Programming (DP)",
    aliases: ["DP", "Bellman Optimization"],
    category: "math",
    definition: "A method for solving complex problems by breaking them down into simpler subproblems, storing solutions to avoid redundant calculations.",
    example: "Determining optimal payment schedule for invoices where each payment affects future cash availability.",
    formula: "V(state) = max{reward + γV(next_state)}"
  },
  {
    term: "Monte Carlo Simulation",
    aliases: ["MC Simulation", "Stochastic Simulation"],
    category: "math",
    definition: "A technique that uses repeated random sampling to obtain numerical results, often used for risk assessment and scenario analysis.",
    example: "Simulating 10,000 scenarios of electricity prices and costs to assess investment risk.",
    formula: "E[X] ≈ (1/N) Σ X_i for large N"
  },
  {
    term: "Latin Hypercube Sampling (LHS)",
    aliases: ["LHS", "Stratified Sampling"],
    category: "math",
    definition: "An efficient sampling technique that divides the probability distribution into equal intervals and samples once from each, ensuring better coverage than pure random sampling.",
    example: "Generating 1,000 diverse scenarios for risk analysis more efficiently than pure random sampling."
  },
  {
    term: "Differential Evolution",
    aliases: ["DE", "Evolutionary Optimization"],
    category: "math",
    definition: "A population-based metaheuristic optimization algorithm that iteratively improves candidate solutions using mutation, crossover, and selection operations.",
    example: "Finding optimal prices for 2,500 products simultaneously, handling complex non-linear demand functions.",
    formula: "x_new = x_a + F(x_b - x_c) if better"
  },
  {
    term: "Elasticity",
    aliases: ["Price Elasticity", "Demand Elasticity"],
    category: "math",
    definition: "The measure of how responsive demand is to changes in price, calculated as the percentage change in quantity divided by percentage change in price.",
    example: "Elasticity of -2.5 means a 10% price increase causes a 25% demand decrease.",
    formula: "ε = (∂Q/∂P) × (P/Q)"
  },
  {
    term: "EOQ (Economic Order Quantity)",
    aliases: ["EOQ", "Wilson Formula"],
    category: "math",
    definition: "The optimal order quantity that minimizes the total cost of ordering and holding inventory.",
    example: "Order 500 units at a time to minimize combined ordering costs ($50/order) and holding costs ($2/unit/year).",
    formula: "EOQ = √(2DS/H) where D=demand, S=order cost, H=holding cost"
  },
  {
    term: "(Q,R) Policy",
    aliases: ["Reorder Point Policy", "Order Quantity-Reorder Point"],
    category: "math",
    definition: "An inventory policy where Q units are ordered whenever inventory falls to reorder point R.",
    example: "Order 500 units (Q) whenever inventory drops to 150 units (R)."
  },
  {
    term: "Convex Optimization",
    aliases: ["Convex Programming"],
    category: "math",
    definition: "Optimization where the objective function is convex and the feasible region is a convex set, guaranteeing that any local optimum is also a global optimum.",
    example: "Minimizing quadratic cost functions subject to linear constraints.",
    formula: "min f(x) where f is convex and constraints form convex set"
  },
  {
    term: "Gradient Descent",
    aliases: ["Steepest Descent"],
    category: "math",
    definition: "An iterative optimization algorithm that moves in the direction of the negative gradient to find a local minimum.",
    example: "Training machine learning models by iteratively adjusting parameters to minimize error.",
    formula: "x_{n+1} = x_n - α∇f(x_n)"
  },
  {
    term: "Lagrange Multiplier",
    aliases: ["Lagrangian", "Shadow Price"],
    category: "math",
    definition: "A value that represents the rate of change of the objective function with respect to relaxing a constraint by one unit.",
    example: "A multiplier of $500 on a capacity constraint means that adding one unit of capacity would increase profit by $500."
  },
  {
    term: "Dual Problem",
    aliases: ["Duality", "Dual Optimization"],
    category: "math",
    definition: "An alternative formulation of an optimization problem that provides bounds on the optimal value and economic interpretation through shadow prices.",
    example: "The dual of a production maximization problem is a cost minimization problem."
  },
  {
    term: "Feasible Region",
    aliases: ["Feasible Set", "Solution Space"],
    category: "math",
    definition: "The set of all points that satisfy all constraints in an optimization problem.",
    example: "All possible price combinations that satisfy minimum margin and maximum discount constraints."
  },
  {
    term: "Global Optimum",
    aliases: ["Global Maximum", "Global Minimum"],
    category: "math",
    definition: "The best solution over the entire feasible region, as opposed to a local optimum which is only better than nearby solutions.",
    example: "The absolute highest profit achievable across all possible pricing strategies."
  },
  {
    term: "Heuristic",
    aliases: ["Rule of Thumb", "Approximation Algorithm"],
    category: "math",
    definition: "A practical approach to problem-solving that finds good-enough solutions quickly, without guaranteeing optimality.",
    example: "Using nearest-neighbor algorithm for route planning instead of checking all possible routes."
  },
  {
    term: "Metaheuristic",
    aliases: ["High-Level Heuristic"],
    category: "math",
    definition: "A higher-level strategy that guides other heuristics to explore the solution space efficiently, often used for complex optimization problems.",
    example: "Genetic algorithms, simulated annealing, and particle swarm optimization."
  },
  {
    term: "Solver",
    aliases: ["Optimization Solver", "Mathematical Solver"],
    category: "technical",
    definition: "Software that implements algorithms to find solutions to optimization problems.",
    example: "CPLEX, Gurobi, IPOPT, SCIP are popular commercial and open-source solvers."
  },
  {
    term: "IPOPT",
    aliases: ["Interior Point Optimizer"],
    category: "technical",
    definition: "An open-source solver for large-scale nonlinear optimization using interior point methods.",
    example: "Used for tax optimization with nonlinear profit allocation across jurisdictions."
  },
  {
    term: "Correlation",
    aliases: ["Linear Correlation", "Pearson Correlation"],
    category: "math",
    definition: "A statistical measure of the linear relationship between two variables, ranging from -1 to +1.",
    example: "Electricity prices and natural gas prices have correlation of 0.78, meaning they tend to move together.",
    formula: "ρ = Cov(X,Y) / (σ_X × σ_Y)"
  },
  {
    term: "Standard Deviation",
    aliases: ["SD", "Volatility", "Sigma"],
    category: "math",
    definition: "A measure of the amount of variation or dispersion in a set of values.",
    example: "A stock with 20% annual standard deviation is more volatile than one with 10%.",
    formula: "σ = √(Σ(x_i - μ)² / N)"
  },
  {
    term: "Cholesky Decomposition",
    aliases: ["Cholesky Factorization"],
    category: "math",
    definition: "A method to decompose a positive-definite matrix into a lower triangular matrix and its transpose, used for generating correlated random variables.",
    example: "Creating correlated price scenarios for multiple commodities in Monte Carlo simulation."
  },
  {
    term: "Regression",
    aliases: ["Linear Regression", "Regression Analysis"],
    category: "math",
    definition: "A statistical method for estimating the relationship between a dependent variable and one or more independent variables.",
    example: "Estimating demand as a function of price, season, and competitor prices.",
    formula: "y = β₀ + β₁x₁ + β₂x₂ + ... + ε"
  },
  {
    term: "Random Forest",
    aliases: ["RF", "Random Decision Forest"],
    category: "math",
    definition: "A machine learning algorithm that creates multiple decision trees and combines their predictions for improved accuracy.",
    example: "Forecasting product demand based on historical sales, seasonality, promotions, and external factors."
  },
  {
    term: "ARIMA",
    aliases: ["AutoRegressive Integrated Moving Average"],
    category: "math",
    definition: "A time series forecasting method that models patterns, trends, and seasonality in historical data.",
    example: "Predicting next month's sales based on patterns in the past 24 months of data.",
    formula: "ARIMA(p,d,q) with p=AR order, d=differencing, q=MA order"
  },
  {
    term: "Fourier Analysis",
    aliases: ["Fourier Series", "Spectral Analysis"],
    category: "math",
    definition: "A method that decomposes a function into sine and cosine waves to identify periodic patterns and seasonality.",
    example: "Detecting weekly and monthly sales patterns in transaction data."
  },
  {
    term: "Confidence Interval",
    aliases: ["CI", "Error Margin"],
    category: "math",
    definition: "A range of values that likely contains the true value of a parameter with a specified probability (e.g., 95% confidence).",
    example: "The true mean profit is between $4.2M and $5.8M with 95% confidence."
  },

  // Finance Terms
  {
    term: "NPV (Net Present Value)",
    aliases: ["NPV", "Present Value"],
    category: "finance",
    definition: "The difference between the present value of cash inflows and outflows over time, used to evaluate investment profitability.",
    example: "An investment with $100K cost and $120K present value of returns has NPV = $20K.",
    formula: "NPV = Σ(CF_t / (1+r)^t) - Initial Investment"
  },
  {
    term: "IRR (Internal Rate of Return)",
    aliases: ["IRR", "Return Rate"],
    category: "finance",
    definition: "The discount rate that makes the NPV of all cash flows equal to zero; represents the expected annual return of an investment.",
    example: "A project with 15% IRR is expected to generate 15% annual returns.",
    formula: "0 = Σ(CF_t / (1+IRR)^t)"
  },
  {
    term: "ROI (Return on Investment)",
    aliases: ["ROI", "Return Rate"],
    category: "finance",
    definition: "A performance measure calculated as net profit divided by investment cost, expressed as a percentage.",
    example: "Investing $100K to save $25K annually gives ROI = 25%.",
    formula: "ROI = (Gain - Cost) / Cost × 100%"
  },
  {
    term: "EBITDA",
    aliases: ["Earnings Before Interest, Taxes, Depreciation, and Amortization"],
    category: "finance",
    definition: "A measure of company profitability that excludes non-operating expenses, used to compare operational performance.",
    example: "A company with $10M revenue, $6M operating expenses has $4M EBITDA (40% margin)."
  },
  {
    term: "ETR (Effective Tax Rate)",
    aliases: ["ETR", "Tax Rate"],
    category: "finance",
    definition: "The average rate at which corporate income is taxed, calculated as total tax expense divided by pre-tax income.",
    example: "Paying $28.5M tax on $100M pre-tax income gives 28.5% ETR.",
    formula: "ETR = Total Tax / Pre-Tax Income × 100%"
  },
  {
    term: "Working Capital",
    aliases: ["Net Working Capital", "Operating Capital"],
    category: "finance",
    definition: "The difference between current assets and current liabilities, representing the liquidity available for day-to-day operations.",
    example: "A company with $50M current assets and $30M current liabilities has $20M working capital.",
    formula: "Working Capital = Current Assets - Current Liabilities"
  },
  {
    term: "DPO (Days Payable Outstanding)",
    aliases: ["DPO", "Payables Days"],
    category: "finance",
    definition: "The average number of days a company takes to pay its suppliers, a measure of working capital efficiency.",
    example: "DPO of 45 days means the company pays suppliers 45 days after receiving invoices on average.",
    formula: "DPO = (Accounts Payable / COGS) × 365"
  },
  {
    term: "DSO (Days Sales Outstanding)",
    aliases: ["DSO", "Receivables Days"],
    category: "finance",
    definition: "The average number of days to collect payment after a sale, measuring how efficiently a company collects receivables.",
    example: "DSO of 30 days means customers pay invoices 30 days after sale on average.",
    formula: "DSO = (Accounts Receivable / Revenue) × 365"
  },
  {
    term: "DIO (Days Inventory Outstanding)",
    aliases: ["DIO", "Inventory Days"],
    category: "finance",
    definition: "The average number of days inventory is held before being sold.",
    example: "DIO of 60 days means inventory sits in warehouse for 60 days on average before sale.",
    formula: "DIO = (Inventory / COGS) × 365"
  },
  {
    term: "Cash Conversion Cycle",
    aliases: ["CCC", "Cash Cycle"],
    category: "finance",
    definition: "The time between paying suppliers and collecting from customers, measuring how efficiently a company manages working capital.",
    example: "CCC = 30 (DSO) + 60 (DIO) - 45 (DPO) = 45 days.",
    formula: "CCC = DSO + DIO - DPO"
  },
  {
    term: "Gross Margin",
    aliases: ["Gross Profit Margin", "GM"],
    category: "finance",
    definition: "Revenue minus cost of goods sold, divided by revenue, representing profitability before operating expenses.",
    example: "Selling products for $100 with $70 cost gives 30% gross margin.",
    formula: "Gross Margin = (Revenue - COGS) / Revenue × 100%"
  },
  {
    term: "Operating Margin",
    aliases: ["Operating Profit Margin", "EBIT Margin"],
    category: "finance",
    definition: "Operating income divided by revenue, measuring profitability from core business operations.",
    example: "A company with $100M revenue and $15M operating income has 15% operating margin.",
    formula: "Operating Margin = Operating Income / Revenue × 100%"
  },
  {
    term: "VaR (Value at Risk)",
    aliases: ["VaR", "Value-at-Risk"],
    category: "finance",
    definition: "The maximum expected loss over a specified time period at a given confidence level.",
    example: "95% VaR of $5M means there's only a 5% chance of losing more than $5M.",
    formula: "P(Loss > VaR) = α (e.g., 5%)"
  },
  {
    term: "CVaR (Conditional Value at Risk)",
    aliases: ["CVaR", "Expected Shortfall", "ES"],
    category: "finance",
    definition: "The expected loss given that the loss exceeds the VaR threshold, providing insight into tail risk.",
    example: "If 95% VaR is $5M and CVaR is $8M, losses exceeding the VaR threshold average $8M.",
    formula: "CVaR = E[Loss | Loss > VaR]"
  },
  {
    term: "Beta",
    aliases: ["Market Beta", "Systematic Risk"],
    category: "finance",
    definition: "A measure of an investment's volatility relative to the overall market; beta > 1 means more volatile than market.",
    example: "A stock with beta = 1.5 tends to move 1.5× as much as the market.",
    formula: "β = Cov(R_asset, R_market) / Var(R_market)"
  },
  {
    term: "WACC (Weighted Average Cost of Capital)",
    aliases: ["WACC", "Cost of Capital"],
    category: "finance",
    definition: "The average rate a company pays for capital from all sources (debt and equity), weighted by their proportions.",
    example: "A company with 60% equity at 12% cost and 40% debt at 6% cost has WACC = 9.6%.",
    formula: "WACC = (E/V)R_e + (D/V)R_d(1-T)"
  },
  {
    term: "Discount Rate",
    aliases: ["Required Return", "Hurdle Rate"],
    category: "finance",
    definition: "The rate used to calculate the present value of future cash flows, reflecting the time value of money and risk.",
    example: "Using 10% discount rate, $100 received in 1 year is worth $90.91 today.",
    formula: "PV = FV / (1 + r)^t"
  },
  {
    term: "CAGR (Compound Annual Growth Rate)",
    aliases: ["CAGR", "Annualized Return"],
    category: "finance",
    definition: "The rate at which an investment would grow if it grew at a steady rate annually over a specified period.",
    example: "Growing from $100M to $150M over 3 years gives CAGR = 14.5%.",
    formula: "CAGR = (FV/PV)^(1/n) - 1"
  },
  {
    term: "Leverage",
    aliases: ["Financial Leverage", "Gearing"],
    category: "finance",
    definition: "The use of debt to amplify potential returns (and risks) of an investment.",
    example: "A company with $60M equity and $40M debt has 40% leverage (debt-to-capital ratio).",
    formula: "Leverage = Debt / (Debt + Equity)"
  },
  {
    term: "Liquidity",
    aliases: ["Cash Liquidity", "Financial Liquidity"],
    category: "finance",
    definition: "The ability to quickly convert assets to cash or meet short-term obligations without significant loss.",
    example: "A company with $10M cash and $5M short-term obligations has high liquidity."
  },
  {
    term: "BEPS (Base Erosion and Profit Shifting)",
    aliases: ["BEPS", "Tax Avoidance"],
    category: "finance",
    definition: "Tax planning strategies used by multinational companies to shift profits to low-tax jurisdictions; subject to increasing regulatory scrutiny.",
    example: "Allocating IP to Ireland (12.5% tax rate) while operating in US (21% rate)."
  },
  {
    term: "Transfer Pricing",
    aliases: ["Intercompany Pricing"],
    category: "finance",
    definition: "Prices charged for goods and services between related entities within a multinational company, subject to arm's length requirements.",
    example: "A US parent charging an Irish subsidiary 10% royalty for using intellectual property."
  },
  {
    term: "Arm's Length Principle",
    aliases: ["Arm's Length Standard"],
    category: "finance",
    definition: "Tax requirement that intercompany transactions must be priced as if between unrelated parties.",
    example: "Transfer pricing for IP royalties must be within 8-15% range based on comparable uncontrolled transactions."
  },
  {
    term: "Fill Rate",
    aliases: ["Order Fill Rate", "Service Level"],
    category: "finance",
    definition: "The percentage of customer orders that can be fulfilled immediately from available inventory.",
    example: "A 98% fill rate means 98 out of 100 orders are fulfilled without stockouts.",
    formula: "Fill Rate = Orders Fulfilled / Total Orders × 100%"
  },
  {
    term: "Inventory Turnover",
    aliases: ["Inventory Turns", "Stock Turnover"],
    category: "finance",
    definition: "The number of times inventory is sold and replaced over a period, measuring inventory efficiency.",
    example: "Inventory turnover of 6x means the company sells its entire inventory 6 times per year.",
    formula: "Inventory Turnover = COGS / Average Inventory"
  },
  {
    term: "Carrying Cost",
    aliases: ["Holding Cost", "Inventory Carrying Cost"],
    category: "finance",
    definition: "The total cost of holding inventory, including storage, insurance, depreciation, and opportunity cost of capital.",
    example: "Typical carrying costs are 20-30% of inventory value annually.",
    formula: "Carrying Cost = (Storage + Insurance + Depreciation + Interest) / Inventory Value"
  },
  {
    term: "Stockout",
    aliases: ["Out of Stock", "Backorder"],
    category: "finance",
    definition: "A situation where demand for a product cannot be met from available inventory.",
    example: "A stockout costs the average retailer $340 in lost margin per incident."
  },
  {
    term: "Safety Stock",
    aliases: ["Buffer Stock", "Reserve Inventory"],
    category: "finance",
    definition: "Extra inventory held to protect against demand variability and supply disruptions.",
    example: "Holding 2 weeks of safety stock to achieve 98% service level during lead time uncertainty.",
    formula: "Safety Stock = Z × σ × √LT"
  },
  {
    term: "Lead Time",
    aliases: ["Delivery Time", "Order Cycle"],
    category: "finance",
    definition: "The time between placing an order and receiving the goods.",
    example: "A supplier with 2-week lead time requires ordering 14 days before inventory is needed."
  },

  // Functional Programming Terms
  {
    term: "Pure Function",
    aliases: ["Deterministic Function"],
    category: "functional",
    definition: "A function that always returns the same output for the same input and has no side effects.",
    example: "calculateDiscount(price, rate) always returns the same result for given inputs without modifying anything.",
    formula: "f(x) = y consistently, with no external state changes"
  },
  {
    term: "Immutability",
    aliases: ["Immutable Data"],
    category: "functional",
    definition: "Data that cannot be modified after creation; operations create new data rather than changing existing data.",
    example: "Creating a new array with updated prices instead of modifying the original array."
  },
  {
    term: "Higher-Order Function",
    aliases: ["HOF", "Functional Combinator"],
    category: "functional",
    definition: "A function that takes functions as arguments or returns functions as results.",
    example: "map(prices, applyDiscount) applies the applyDiscount function to each price in the array."
  },
  {
    term: "Map",
    aliases: ["Array.map", "Transform"],
    category: "functional",
    definition: "A higher-order function that transforms each element of a collection using a provided function.",
    example: "prices.map(p => p * 1.1) increases each price by 10%."
  },
  {
    term: "Filter",
    aliases: ["Array.filter", "Select"],
    category: "functional",
    definition: "A higher-order function that selects elements from a collection that satisfy a predicate.",
    example: "products.filter(p => p.margin > 0.3) returns only products with margin above 30%."
  },
  {
    term: "Reduce",
    aliases: ["Fold", "Aggregate", "Array.reduce"],
    category: "functional",
    definition: "A higher-order function that combines all elements of a collection into a single value.",
    example: "prices.reduce((sum, p) => sum + p, 0) calculates total revenue."
  },
  {
    term: "Currying",
    aliases: ["Partial Application"],
    category: "functional",
    definition: "Transforming a function with multiple arguments into a sequence of functions each taking a single argument.",
    example: "applyDiscount(rate)(price) instead of applyDiscount(rate, price)."
  },
  {
    term: "Composition",
    aliases: ["Function Composition", "Pipe"],
    category: "functional",
    definition: "Combining multiple functions to create a new function, where the output of one function becomes the input of the next.",
    example: "compose(formatCurrency, applyTax, calculatePrice)(item) chains three operations."
  },
  {
    term: "Lambda",
    aliases: ["Anonymous Function", "Arrow Function"],
    category: "functional",
    definition: "A function defined without a name, often used for short operations passed to higher-order functions.",
    example: "(x) => x * 2 is a lambda that doubles its input."
  },
  {
    term: "Closure",
    aliases: ["Lexical Closure"],
    category: "functional",
    definition: "A function that captures and remembers variables from its surrounding scope.",
    example: "A discount calculator that remembers the discount rate from its creation context."
  },
  {
    term: "Recursion",
    aliases: ["Recursive Function"],
    category: "functional",
    definition: "A function that calls itself to solve problems by breaking them into smaller subproblems.",
    example: "Calculating factorial: factorial(n) = n × factorial(n-1), base case: factorial(0) = 1."
  },
  {
    term: "Memoization",
    aliases: ["Function Caching"],
    category: "functional",
    definition: "Caching function results to avoid redundant calculations for the same inputs.",
    example: "Storing elasticity calculations so they don't need to be recalculated for the same product."
  },
  {
    term: "Lazy Evaluation",
    aliases: ["Deferred Evaluation", "Call-by-need"],
    category: "functional",
    definition: "Delaying computation until the result is actually needed.",
    example: "Generating price scenarios only when they're actually used in calculations."
  },
  {
    term: "Side Effect",
    aliases: ["State Mutation", "External Change"],
    category: "functional",
    definition: "Any operation that modifies state outside its local scope or has observable interaction with the outside world.",
    example: "Modifying a global variable, writing to database, or printing to console."
  },

  // Technical Terms & Acronyms
  {
    term: "API (Application Programming Interface)",
    aliases: ["API", "Web API", "REST API"],
    category: "technical",
    definition: "A set of rules and protocols that allows different software applications to communicate with each other.",
    example: "The optimization API accepts pricing parameters and returns optimal prices."
  },
  {
    term: "REST (Representational State Transfer)",
    aliases: ["REST", "RESTful"],
    category: "technical",
    definition: "An architectural style for designing networked applications using HTTP methods and stateless communication.",
    example: "POST /api/optimize-pricing sends data to start an optimization job."
  },
  {
    term: "JSON (JavaScript Object Notation)",
    aliases: ["JSON"],
    category: "technical",
    definition: "A lightweight data format for exchanging information between systems using human-readable text.",
    example: '{"product_id": "SKU123", "price": 49.99, "category": "electronics"}'
  },
  {
    term: "SKU (Stock Keeping Unit)",
    aliases: ["SKU", "Product Code"],
    category: "technical",
    definition: "A unique identifier for each distinct product and service that can be purchased.",
    example: "SKU-LAPTOP-15-BLK identifies a 15-inch black laptop model."
  },
  {
    term: "ETL (Extract, Transform, Load)",
    aliases: ["ETL", "Data Pipeline"],
    category: "technical",
    definition: "The process of extracting data from sources, transforming it to fit operational needs, and loading it into a target database.",
    example: "Extracting sales data from POS systems, calculating metrics, and loading into analytics database."
  },
  {
    term: "COGS (Cost of Goods Sold)",
    aliases: ["COGS", "Direct Costs"],
    category: "finance",
    definition: "The direct costs attributable to the production of goods sold by a company.",
    example: "For a retailer, COGS includes wholesale purchase price and freight costs.",
    formula: "COGS = Beginning Inventory + Purchases - Ending Inventory"
  },
  {
    term: "KPI (Key Performance Indicator)",
    aliases: ["KPI", "Performance Metric"],
    category: "technical",
    definition: "A measurable value that demonstrates how effectively a company is achieving key business objectives.",
    example: "Revenue growth, gross margin, inventory turnover, and customer satisfaction are common KPIs."
  },
  {
    term: "SLA (Service Level Agreement)",
    aliases: ["SLA", "Service Contract"],
    category: "technical",
    definition: "A commitment between a service provider and customer defining the expected level of service.",
    example: "A 98% fill rate SLA means the supplier commits to fulfilling 98% of orders immediately."
  },
  {
    term: "ML (Machine Learning)",
    aliases: ["ML", "AI", "Artificial Intelligence"],
    category: "technical",
    definition: "Algorithms that learn patterns from data to make predictions or decisions without explicit programming.",
    example: "Using ML to forecast demand based on historical sales, seasonality, and external factors."
  },
  {
    term: "CI/CD (Continuous Integration/Continuous Deployment)",
    aliases: ["CI/CD", "DevOps Pipeline"],
    category: "technical",
    definition: "Practices that enable frequent, reliable software releases through automated building, testing, and deployment.",
    example: "Automatically testing and deploying pricing algorithm updates when code is committed."
  },
  {
    term: "SaaS (Software as a Service)",
    aliases: ["SaaS", "Cloud Software"],
    category: "technical",
    definition: "Software delivery model where applications are hosted by a provider and accessed via the internet.",
    example: "ProFinTech optimization platform delivered as a cloud-based SaaS application."
  },
  {
    term: "GUI (Graphical User Interface)",
    aliases: ["GUI", "UI", "User Interface"],
    category: "technical",
    definition: "A visual interface that allows users to interact with software through graphical elements rather than text commands.",
    example: "The dashboard displaying optimization results with charts and interactive controls."
  },
  {
    term: "CRUD (Create, Read, Update, Delete)",
    aliases: ["CRUD", "Data Operations"],
    category: "technical",
    definition: "The four basic operations for persistent storage and database management.",
    example: "Users can create new products, read product data, update prices, and delete obsolete items."
  },
  {
    term: "SQL (Structured Query Language)",
    aliases: ["SQL", "Database Query"],
    category: "technical",
    definition: "A programming language for managing and querying relational databases.",
    example: "SELECT * FROM products WHERE category = 'electronics' AND margin > 0.3"
  },
  {
    term: "NoSQL",
    aliases: ["Non-relational Database", "Document Store"],
    category: "technical",
    definition: "Database systems that don't use traditional table-based relational structures, designed for flexibility and scale.",
    example: "MongoDB, Cassandra, and Redis are popular NoSQL databases."
  },
  {
    term: "HTTPS (Hypertext Transfer Protocol Secure)",
    aliases: ["HTTPS", "Secure HTTP"],
    category: "technical",
    definition: "Encrypted version of HTTP for secure communication over computer networks.",
    example: "Using HTTPS ensures pricing data transmitted to the API is encrypted."
  },
  {
    term: "OAuth",
    aliases: ["OAuth 2.0", "Authentication Protocol"],
    category: "technical",
    definition: "An authorization framework that enables secure delegated access to resources.",
    example: "Logging in with Google or Microsoft accounts using OAuth."
  },
  {
    term: "JWT (JSON Web Token)",
    aliases: ["JWT", "Access Token"],
    category: "technical",
    definition: "A compact token format for securely transmitting information between parties as a JSON object.",
    example: "After login, the API returns a JWT token used to authenticate subsequent requests."
  },
  {
    term: "Latency",
    aliases: ["Response Time", "Lag"],
    category: "technical",
    definition: "The time delay between a request and response, measuring system responsiveness.",
    example: "API latency of 200ms means optimization requests receive responses in 200 milliseconds."
  },
  {
    term: "Throughput",
    aliases: ["Processing Rate", "Bandwidth"],
    category: "technical",
    definition: "The amount of work or data processed per unit time.",
    example: "The system can process 1,000 optimization requests per minute."
  },
  {
    term: "Scalability",
    aliases: ["Horizontal Scaling", "Vertical Scaling"],
    category: "technical",
    definition: "The ability of a system to handle increased load by adding resources.",
    example: "Adding more servers to handle optimization requests during peak hours."
  },
  {
    term: "Load Balancing",
    aliases: ["Load Distribution"],
    category: "technical",
    definition: "Distributing workload across multiple computing resources to optimize performance.",
    example: "Distributing optimization requests across 5 servers to handle high traffic."
  },
  {
    term: "Cache",
    aliases: ["Caching", "Data Cache"],
    category: "technical",
    definition: "Temporary storage of frequently accessed data to speed up retrieval.",
    example: "Caching elasticity calculations to avoid recalculating for the same products."
  },
  {
    term: "Microservices",
    aliases: ["Service-Oriented Architecture", "Distributed Services"],
    category: "technical",
    definition: "Architectural style structuring an application as a collection of loosely coupled services.",
    example: "Separate services for pricing optimization, inventory management, and tax calculation."
  },
  {
    term: "Docker",
    aliases: ["Container", "Containerization"],
    category: "technical",
    definition: "A platform for developing, shipping, and running applications in isolated containers.",
    example: "Packaging the optimization solver in a Docker container for consistent deployment."
  },
  {
    term: "Kubernetes",
    aliases: ["K8s", "Container Orchestration"],
    category: "technical",
    definition: "An open-source system for automating deployment, scaling, and management of containerized applications.",
    example: "Using Kubernetes to automatically scale optimization services based on demand."
  },
  {
    term: "Regression Testing",
    aliases: ["Integration Testing"],
    category: "technical",
    definition: "Testing to ensure that new changes don't break existing functionality.",
    example: "Running automated tests after updating pricing algorithms to verify all modules still work."
  },
  {
    term: "A/B Testing",
    aliases: ["Split Testing", "Bucket Testing"],
    category: "technical",
    definition: "Comparing two versions of something to determine which performs better.",
    example: "Testing optimized prices on 50% of customers to measure impact before full rollout."
  },
  {
    term: "Benchmark",
    aliases: ["Performance Baseline", "Reference Point"],
    category: "technical",
    definition: "A standard or point of reference against which performance can be measured.",
    example: "Industry benchmark for inventory turnover is 3.5x; company currently at 2.0x."
  }
];

const Glossary: React.FC = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState<string>('all');
  const [expandedTerms, setExpandedTerms] = useState<Set<string>>(new Set());

  const categories = [
    { id: 'all', name: 'All Terms', icon: BookOpen },
    { id: 'math', name: 'Mathematics', icon: Hash },
    { id: 'finance', name: 'Finance', icon: TrendingUp },
    { id: 'functional', name: 'Functional Programming', icon: Code },
    { id: 'technical', name: 'Technical', icon: BookOpen }
  ];

  const filteredTerms = glossaryTerms.filter(term => {
    const matchesSearch = searchTerm === '' ||
      term.term.toLowerCase().includes(searchTerm.toLowerCase()) ||
      term.definition.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (term.aliases?.some(alias => alias.toLowerCase().includes(searchTerm.toLowerCase())) ?? false);

    const matchesCategory = selectedCategory === 'all' || term.category === selectedCategory;

    return matchesSearch && matchesCategory;
  });

  const toggleTerm = (term: string) => {
    const newExpanded = new Set(expandedTerms);
    if (newExpanded.has(term)) {
      newExpanded.delete(term);
    } else {
      newExpanded.add(term);
    }
    setExpandedTerms(newExpanded);
  };

  const getCategoryColor = (category: string) => {
    switch (category) {
      case 'math': return 'bg-blue-100 text-blue-800 border-blue-200';
      case 'finance': return 'bg-green-100 text-green-800 border-green-200';
      case 'functional': return 'bg-purple-100 text-purple-800 border-purple-200';
      case 'technical': return 'bg-orange-100 text-orange-800 border-orange-200';
      default: return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-blue-50">
      <div className="max-w-7xl mx-auto px-8 py-12">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-4xl font-bold text-gray-900 mb-3">Glossary</h1>
          <p className="text-lg text-gray-600">
            Comprehensive reference of mathematical, financial, functional programming, and technical terms
          </p>
        </div>

        {/* Search Bar */}
        <div className="mb-6">
          <div className="relative">
            <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
            <input
              type="text"
              placeholder="Search terms, definitions, or aliases..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-12 pr-4 py-3 border-2 border-gray-200 rounded-xl focus:border-primary-500 focus:ring-2 focus:ring-primary-200 transition-all"
            />
          </div>
        </div>

        {/* Category Filters */}
        <div className="mb-8 flex flex-wrap gap-3">
          {categories.map(category => {
            const Icon = category.icon;
            return (
              <button
                key={category.id}
                onClick={() => setSelectedCategory(category.id)}
                className={`px-5 py-2.5 rounded-lg font-medium transition-all flex items-center space-x-2 ${
                  selectedCategory === category.id
                    ? 'bg-primary-600 text-white shadow-lg'
                    : 'bg-white text-gray-700 hover:bg-gray-50 border-2 border-gray-200'
                }`}
              >
                <Icon className="w-4 h-4" />
                <span>{category.name}</span>
              </button>
            );
          })}
        </div>

        {/* Results Count */}
        <div className="mb-4 text-sm text-gray-600">
          Showing {filteredTerms.length} of {glossaryTerms.length} terms
        </div>

        {/* Terms List */}
        <div className="space-y-4">
          {filteredTerms.map((term, index) => {
            const isExpanded = expandedTerms.has(term.term);

            return (
              <div
                key={index}
                className="bg-white rounded-xl shadow-md hover:shadow-lg transition-all border-2 border-gray-100"
              >
                <div
                  className="p-6 cursor-pointer"
                  onClick={() => toggleTerm(term.term)}
                >
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center space-x-3 mb-2">
                        <h3 className="text-xl font-bold text-gray-900">{term.term}</h3>
                        <span className={`px-3 py-1 rounded-full text-xs font-semibold border ${getCategoryColor(term.category)}`}>
                          {term.category}
                        </span>
                      </div>

                      {term.aliases && term.aliases.length > 0 && (
                        <div className="mb-3">
                          <span className="text-sm text-gray-500 italic">
                            Also known as: {term.aliases.join(', ')}
                          </span>
                        </div>
                      )}

                      <p className="text-gray-700 leading-relaxed">{term.definition}</p>
                    </div>
                  </div>
                </div>

                {isExpanded && (term.example || term.formula) && (
                  <div className="px-6 pb-6 space-y-4 border-t border-gray-100 pt-4">
                    {term.formula && (
                      <div className="bg-blue-50 rounded-lg p-4 border-l-4 border-blue-400">
                        <p className="text-sm font-semibold text-blue-900 mb-2">Formula</p>
                        <code className="text-sm text-blue-800 font-mono">{term.formula}</code>
                      </div>
                    )}

                    {term.example && (
                      <div className="bg-green-50 rounded-lg p-4 border-l-4 border-green-400">
                        <p className="text-sm font-semibold text-green-900 mb-2">Example</p>
                        <p className="text-sm text-green-800">{term.example}</p>
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {filteredTerms.length === 0 && (
          <div className="text-center py-12">
            <p className="text-gray-500 text-lg">No terms found matching your search.</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default Glossary;
